import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useContent } from "../../hooks/useContent";
import CustomMUITable from "../../Components/GeneralComponents/CustomMUITable/CustomMUITable";
import PageLoader from "../../Components/GeneralComponents/PageLoader";
import {
  get_organization_delete_api,
  get_organizations_api,
} from "../../DAL/Organization";
import ConfirmationPopup from "../../Components/GeneralComponents/ConfirmationPopup";

const ManageOrganizations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { setPageHeading } = useContent();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("organization_name");
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [updatedOrganizations, setUpdatedOrganizations] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedObject, setSelectedObject] = useState({});

  const loadOrganizations = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingPage(true);
    const result = await get_organizations_api();
    if (result.code === 200 && result.organizations) {
      const data = result.organizations || [];
      // Apply client-side search filter if needed
      let filteredData = data;
      if (search) {
        filteredData = data.filter(
          (org) =>
            org.organization_name
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            org.slug?.toLowerCase().includes(search.toLowerCase()),
        );
      }
      // Apply client-side sort if needed
      if (sort === "organization_name") {
        filteredData.sort((a, b) =>
          (a.organization_name || "").localeCompare(b.organization_name || ""),
        );
      } else if (sort === "created_at" || sort === "creation_date") {
        filteredData.sort(
          (a, b) => new Date(b.creation_date) - new Date(a.creation_date),
        );
      }
      const numbered = filteredData.map((org, index) => ({
        ...org,
        number: index + 1,
        organization_name: org.organization_name,
        organization_id: org.organization_id,
        // Format creation date for display
        creation_date: org.creation_date
          ? dayjs(org.creation_date).format("DD/MM/YYYY")
          : "",
      }));
      setUpdatedOrganizations(numbered);
    } else {
      enqueueSnackbar(`${result.message || "Failed to load organizations"}`, {
        variant: "error",
      });
    }
    setIsLoadingPage(false);
  };

  useEffect(() => {
    setPageHeading("Existing Organizations Licenses");
    loadOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial list load once on mount
  }, []);

  const handleChangeSearch = (e) => setSearch(e.target.value);
  const handleChangeSort = (e) => setSort(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadOrganizations();
  };
  const handleBack = () => navigate("/home");
  const handleCreate = () => navigate("/create-organization");

  const handleEditOrganization = (organization) => {
    navigate(`/manage-organizations/update-organization/${organization.organization_id}`);
  };

  const handleDeleteOrganization = async () => {
    setIsDeleting(true);
    const result = await get_organization_delete_api(selectedObject.organization_id);
    if (result.code === 200) {
      setUpdatedOrganizations((old) =>
        old.filter(
          (organization) => organization.organization_id !== selectedObject.organization_id,
        ),
      );
      setIsDeleting(false);
      setOpenDelete(false);
      setSelectedObject(null);
      enqueueSnackbar(`${result.message}`, { variant: "success" });
    } else {
      enqueueSnackbar(`Error deleting organization: ${result.message}`, {
        variant: "error",
      });
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedObject(item);
    setOpenDelete(true);
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "mdi:pencil",
      handleClick: handleEditOrganization,
    },
    {
      label: "Delete",
      icon: "mdi:trash-can",
      handleClick: handleDeleteClick,
    },
  ];

  if (isLoadingPage) return <PageLoader />;

  const TABLE_HEAD = [
    { id: "number", label: "#", type: "number" },
    { id: "organization_name", label: "Organization Name" },
    { id: "creation_date", label: "Created Date" },
    { id: "website", label: "Website" },
    { id: "actions", label: "Actions", type: "action" },
  ];

  return (
    <div
      className="login-page container"
      style={{ paddingLeft: "0", paddingRight: "0" }}
    >
      <div className="d-flex" style={{ width: "100%", maxWidth: "100%" }}>
        <div
          className="container mx-auto"
          style={{
            maxWidth: "100%",
            width: "100%",
            paddingLeft: "0.5rem",
            paddingRight: "0.5rem",
          }}
        >
          <div
            className="form-wrapper"
            style={{
              maxWidth: "100%",
              width: "100%",
              paddingLeft: "1.5rem",
              paddingRight: "1.5rem",
              paddingTop: "1.5rem",
              paddingBottom: "1.5rem",
            }}
          >
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col">
                  <div className="col">
                    <div className="d-flex justify-content-between action-buttons-row">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={handleBack}
                      >
                        <Icon icon="mdi:arrow-left" /> Back
                      </button>
                      <button
                        type="button"
                        className="theme-button"
                        onClick={handleCreate}
                      >
                        <Icon icon="mdi:plus" /> Create Organization
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col">
                  <div className="search_sort_box d-flex gap-2 align-items-center">
                    <div className="form-group flex-1">
                      <label>Search</label>
                      <div className="input-wrapper">
                        <Icon icon="mdi:magnify" className="input-icon" />
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search organizations..."
                          value={search}
                          onChange={handleChangeSearch}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Sort By</label>
                      <div className="input-wrapper">
                        <Icon
                          icon="mdi:filter-variant"
                          className="input-icon"
                        />
                        <select
                          className="form-select"
                          value={sort}
                          onChange={handleChangeSort}
                        >
                          <option value="organization_name">
                            Organization Name
                          </option>
                          <option value="created_at">Created Date</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label style={{ visibility: "hidden" }}>Go</label>
                      <button
                        type="submit"
                        className="theme-button"
                        style={{ width: "100%" }}
                      >
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "2rem",
                  marginBottom: "2rem",
                  padding: "1.5rem 0",
                }}
              >
                <CustomMUITable
                  TABLE_HEAD={TABLE_HEAD}
                  data={updatedOrganizations}
                  MENU_OPTIONS={MENU_OPTIONS}
                  hide_search={true}
                />
              </div>
              <ConfirmationPopup
                openDelete={openDelete}
                setOpenDelete={setOpenDelete}
                title={"Are you sure you want to delete this organization?"}
                handleAgree={handleDeleteOrganization}
                isLoading={isDeleting}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageOrganizations;
